import { useEffect, useRef } from 'react';
import 'maplibre-gl/dist/maplibre-gl.css';
import { activeAhead, edgeBearings, headlandRing, type LonLat } from '@fla/core';
import { MapController } from '../map/controller';
import { mapRef } from '../map/mapRef';
import { useStore } from '../store';

export function MapView() {
  const containerRef = useRef<HTMLDivElement>(null);
  const ctrlRef = useRef<MapController | null>(null);

  const fix = useStore((s) => s.fix);
  const fields = useStore((s) => s.fields);
  const selectedField = useStore((s) => s.selectedField);
  const course = useStore((s) => s.course);
  const phase = useStore((s) => s.phase);
  const trail = useStore((s) => s.trail);
  const coverageGeo = useStore((s) => s.coverageGeo);
  const editMode = useStore((s) => s.editMode);
  const editRev = useStore((s) => s.editRev);
  const draftRing = useStore((s) => s.draftRing);
  const zones = useStore((s) => s.zones);
  const selectedZoneIndex = useStore((s) => s.selectedZoneIndex);
  const widthM = useStore((s) => s.widthM);
  const headlandRounds = useStore((s) => s.headlandRounds);
  const headingDeg = useStore((s) => s.headingDeg);

  // Init once.
  useEffect(() => {
    if (!containerRef.current) return;
    const start = useStore.getState().fix;
    // Fallback before the first fix arrives: the beta-test site (49°55'34.7"N 9°58'46.8"E, near Würzburg).
    const ctrl = new MapController(
      containerRef.current,
      start ? [start.lon, start.lat] : [9.97967, 49.92631],
    );
    // Field tap selects — unless we're in an edit mode (then the map click is for drawing/zones).
    ctrl.onField = (f) => {
      if (useStore.getState().editMode === 'none') useStore.getState().selectField(f);
    };
    ctrl.onMapClick = (p) => useStore.getState().handleMapClick(p);
    ctrl.onZoneClick = (i) => useStore.getState().selectZone(i);
    ctrl.onFollowChange = (f) => useStore.getState().setCameraFollowing(f);
    ctrlRef.current = ctrl;
    mapRef.current = ctrl;
    (window as unknown as Record<string, unknown>).__flaMap = ctrl;
    // Resume the browser test-sim if it was left on (persisted) — the map now has a valid center.
    if (useStore.getState().simEnabled) useStore.getState().startClientSim();
    return () => {
      mapRef.current = null;
      ctrl.destroy();
    };
  }, []);

  useEffect(() => ctrlRef.current?.setFields(fields), [fields]);
  useEffect(() => ctrlRef.current?.setSelected(selectedField), [selectedField]);
  useEffect(() => ctrlRef.current?.setCourse(course), [course]);
  useEffect(() => ctrlRef.current?.setVehicle(fix), [fix]);
  useEffect(() => ctrlRef.current?.setTrail(trail), [trail]);
  useEffect(() => ctrlRef.current?.setCoverage(coverageGeo), [coverageGeo]);
  useEffect(() => ctrlRef.current?.setDraft(draftRing), [draftRing]);
  useEffect(() => ctrlRef.current?.setZones(zones), [zones]);

  // Headland boundary ring (the turning margin), drawn when a headland is set.
  useEffect(() => {
    const c = ctrlRef.current;
    if (!c) return;
    const ring = selectedField ? (selectedField.geometry.coordinates[0] as LonLat[]) : null;
    c.setHeadland(ring && headlandRounds > 0 ? headlandRing(ring, headlandRounds * widthM) : []);
  }, [selectedField, headlandRounds, widthM]);

  // Draggable heading arrow at the field centroid (field phase only).
  useEffect(() => {
    const c = ctrlRef.current;
    if (!c) return;
    if (phase === 'field' && selectedField) {
      const ring = selectedField.geometry.coordinates[0] as LonLat[];
      let sx = 0;
      let sy = 0;
      let n = 0;
      for (let i = 0; i + 1 < ring.length; i++) {
        sx += ring[i]![0];
        sy += ring[i]![1];
        n += 1;
      }
      const centroid: LonLat = n > 0 ? [sx / n, sy / n] : ring[0]!;
      // Sensible snap angles: each field-edge bearing + its perpendiculars.
      const snaps = edgeBearings(ring).flatMap((b) => [
        b,
        (b + 90) % 360,
        (b + 180) % 360,
        (b + 270) % 360,
      ]);
      c.setHeadingArrow(centroid, headingDeg ?? 0, snaps, (b) =>
        useStore.getState().setHeadingFromDrag(b),
      );
    } else {
      c.setHeadingArrow(null, 0, []);
    }
  }, [phase, selectedField, headingDeg]);

  // Drag handles for editing a field outline. Recreated on entering edit mode AND when editRev bumps
  // (a vertex was inserted by tapping an edge) — but NOT on every vertex drag, so a drag isn't
  // interrupted mid-gesture.
  useEffect(() => {
    const c = ctrlRef.current;
    if (!c) return;
    if (editMode === 'editField') {
      const f = useStore.getState().selectedField;
      const ring = f ? (f.geometry.coordinates[0] as LonLat[]).slice(0, -1) : [];
      c.setEditHandles(
        ring,
        (i, p) => useStore.getState().updateFieldVertex(i, p),
        (i) => useStore.getState().removeFieldVertex(i),
      );
    } else {
      c.setEditHandles(null);
    }
  }, [editMode, editRev]);

  // Draggable center handle for the selected zone (move it). Depends only on mode + which zone is
  // selected — NOT on the zone's center, so dragging doesn't recreate the handle mid-gesture.
  useEffect(() => {
    const c = ctrlRef.current;
    if (!c) return;
    const s = useStore.getState();
    if (editMode === 'addZone' && s.selectedZoneIndex != null && s.zones[s.selectedZoneIndex]) {
      const idx = s.selectedZoneIndex;
      c.setZoneHandle(s.zones[idx]!.center, (p) => useStore.getState().updateZoneCenter(idx, p));
    } else {
      c.setZoneHandle(null);
    }
  }, [editMode, selectedZoneIndex]);

  // Phase transitions: toggle the field layer + camera mode. `paused` holds the heading-up chase
  // view (freeze, don't reframe to overview); resuming re-attaches without re-snapping the user's
  // zoom (resumeFollow, not beginDriving — that's only for a fresh GO).
  const prevPhaseRef = useRef<string>('idle');
  useEffect(() => {
    const c = ctrlRef.current;
    if (!c) return;
    const prev = prevPhaseRef.current;
    prevPhaseRef.current = phase;
    if (phase === 'driving') {
      c.setFieldsVisible(false);
      if (prev === 'paused') c.resumeFollow(useStore.getState().fix);
      else c.beginDriving();
    } else if (phase === 'paused') {
      c.setFieldsVisible(false); // keep the chase view + active segment; just stop moving
    } else {
      c.setFieldsVisible(true);
      c.overviewCamera();
      c.setActiveSegment([]);
    }
  }, [phase]);

  // Driving: chase camera + highlight only the next ~50 m of the active lane.
  useEffect(() => {
    const c = ctrlRef.current;
    if (!c || phase !== 'driving') return;
    if (fix) {
      c.driveCamera(fix);
      if (course) c.setActiveSegment(activeAhead(course, { lat: fix.lat, lon: fix.lon }, 50));
    }
  }, [fix, course, phase]);

  return <div ref={containerRef} className="absolute inset-0" />;
}
